import { httpClient } from "@bw/shared/api"
import type {
  ReservationQuoteRequest,
  ReservationQuoteResponse,
  ReservationQuoteLineItemResponse,
  PriceQuoteTrace,
  RuleTraceEntry,
} from "@bw/shared/types"

// Reservation quote preview client (F4 Slice 2). The FE owns NO pricing: it
// forwards what the operator selected and renders ONLY the numbers the backend
// returns. It never sums line items, never computes a subtotal/total.
//
// Backend contract (backend/app/routers/facility.py — prefix "/facilities"):
//   POST /facilities/{facility_id}/reservation-quote
//        body ReservationQuoteRequest -> ReservationQuoteResponse
//
// Types derive from the generated OpenAPI types (@bw/shared/types) so they stay
// in lockstep with backend Pydantic. The generated money fields are branded
// `Money`; we widen them to `number | string` at this boundary because that is
// the actual wire shape (Pydantic Decimal serializes as a string) and money is
// rendered through the shared `formatMoney` helper, never via raw arithmetic.

type MoneyWire = number | string

export type QuoteLineItem = Omit<
  ReservationQuoteLineItemResponse,
  "amount" | "total"
> & { amount: MoneyWire; total: MoneyWire }

// The "why this price" trace — the SERVER's explanation. Each candidate is a
// matched rule (its name/summary, priority, won flag, tiebreak reason). The
// money in charge_breakdown is widened to the wire shape (Decimal-as-string)
// so the simulator renders it through formatMoney, never via arithmetic. The
// FE never decides the winner — `won` / `winning_rule_id` come from the server.
export type QuoteTraceEntry = RuleTraceEntry

export type QuoteTrace = Omit<PriceQuoteTrace, "charge_breakdown"> & {
  charge_breakdown?: QuoteLineItem[]
}

// `trace` is OPTIONAL and nullable: a plain (explain:false) quote carries no
// trace, and a server that returns one yields it here verbatim — the client
// never synthesizes one. It is NOT Omit-stripped, so `quote.trace?.candidates`
// / `.winning_rule_id` / `.charge_breakdown` type-check at the call site.
export type QuoteResponse = Omit<
  ReservationQuoteResponse,
  "subtotal" | "total" | "line_items" | "trace"
> & {
  subtotal: MoneyWire
  total: MoneyWire
  line_items?: QuoteLineItem[]
  trace?: QuoteTrace | null
}

export function getReservationQuote(
  facilityId: string,
  body: ReservationQuoteRequest,
): Promise<QuoteResponse> {
  return httpClient.post<QuoteResponse>(
    `/facilities/${facilityId}/reservation-quote`,
    body,
  )
}
